import { apiCart } from '@api/cart';
import { apiUser } from '@api/user';
import { CardComponent } from '@chargebee/chargebee-js-vue-wrapper';
import { pushDataLayerEvent } from '@helpers/dataLayer';
import { useFetch } from '@helpers/use-fetch';
import { CartItemPayload } from '@models/cart';
import { CartProduct, PRODUCT_TYPE } from '@models/product';
import { VARIANT_TYPE } from '@models/variant';
import { useQuizStore } from '@stores/quiz';
import { omit, set, cloneDeep, sumBy } from 'lodash';
import { defineStore } from 'pinia';
import { computed, nextTick, ref, watch } from 'vue';
import { useToast } from 'vue-toast-notification';
import { useConsentForm } from './composables/useConsentForm';
import { useForms } from './composables/useForms';
import { useProfile } from './composables/useProfile';
import { useAnalyticsStore, useCartStore, useUserStore } from '@/js/stores';

export enum PaymentMethodTypes {
  Card= 'CARD',
  ApplePay = 'APPLE_PAY',
}

export const useCheckoutStore = defineStore('checkout', () => {
  const instance = window.Chargebee?.init({
    site: import.meta.env.VITE_CHARGEBEE_API_SITE,
    publishableKey: import.meta.env.VITE_CHARGEBEE_PUBLISHABLE_KEY,
  });

  const toast = useToast();

  const quizStore = useQuizStore();
  const analyticsStore = useAnalyticsStore();
  const cartStore = useCartStore();
  const userStore = useUserStore();

  const paymentMethodType = ref<PaymentMethodTypes>(PaymentMethodTypes.Card);

  const applePayHandler = ref();
  instance?.load('apple-pay').then((handler) => {
    applePayHandler.value = handler;
  });
  const isApplePayCompatible = computed(() => {
    return applePayHandler.value?.canMakePayments() ?? false;
  });

  const createApplePaymentIntentApi = useFetch(apiCart.createApplePaymentIntent);
  const createApplePaymentIntentData = computed(() => createApplePaymentIntentApi.response.value?.data);
  const createApplePaymentIntentLoading = computed(() => createApplePaymentIntentApi.loading.value);

  const isApplePayInited = ref(false);
  const applePayPaymentIntentId = ref<string|null>(null);

  const initApplePay = async () => {
    if (isApplePayInited.value) {
      return;
    }

    await createApplePaymentIntentApi.execute({
      amount: discountedNowPrice.value,
    });
    applePayHandler.value.setPaymentIntent(createApplePaymentIntentData.value);

    applePayHandler.value.mountPaymentButton('#apple-pay-button')
      .then(() => {
        isApplePayInited.value = true;

        return applePayHandler.value.handlePayment({
          success: (data: any) => {
            applePayPaymentIntentId.value = data.paymentIntent.id;
          },
          error: (paymentIntent: any) => {
            const paymentIntentError = paymentIntent?.active_payment_attempt?.error_detail?.error_message;
            // eslint-disable-next-line no-console
            console.log(paymentIntent);
            toast.error(paymentIntentError || 'Apple Pay error');
          },
        });
      })
      .catch((error: any) => {
        toast.error('Apple Pay error');
        // eslint-disable-next-line no-console
        console.log(error);
        throw new Error(`Apple Pay error: ${error.message}`);
      });
  };

  const { profileUser, profileBillingAddress, profileShippingAddress } = useProfile();

  const products = ref<CartProduct[]>([]);
  const mainProduct = ref<CartProduct | null>(null);

  const membershipProduct = computed(() => {
    return products.value.find((p) => {
      return p.product.productType.includes(PRODUCT_TYPE.DIGITAL)
          && p.product.variants.find((variant) => variant.variantType === VARIANT_TYPE.SINGLE);
    });
  });

  const isBillingAddressSame = ref(true);

  // Payment methods
  const getPaymentMethodsApi = useFetch(apiUser.getPaymentMethods);
  const paymentMethods = computed(() => getPaymentMethodsApi.response.value?.data.paymentMethods || []);
  const activePaymentMethodId = computed(() => paymentMethods.value.find((item) => item.card.default)?.externalRef || null);
  getPaymentMethodsApi.execute({});

  const isNewPaymentMethod = ref(false);

  watch(paymentMethods, (paymentMethods) => {
    isNewPaymentMethod.value = paymentMethods.length === 0;
  });

  const deletePaymentMethodApi = useFetch(apiUser.deletePaymentMethod);
  // const deletePaymentMethodLoading = computed(() => deletePaymentMethodApi.loading.value);
  const deletePaymentMethod = (paymentSourceId: string) => deletePaymentMethodApi.execute({ paymentSourceId });

  const cardComponent = ref<InstanceType<typeof CardComponent> | null>(null);
  const paymentSection = ref<HTMLElement | null>(null);

  const {
    checkoutForm,
    checkout$,
    variantForm,
    variant$,
    checkboxesForm,
    checkboxes$,
  } = useForms(isBillingAddressSame, isNewPaymentMethod);

  const { consentForm } = useConsentForm();

  watch(paymentMethodType, (value) => {
    checkoutForm.value.paymentMethodType = value;
    if (value === PaymentMethodTypes.ApplePay) {
      initApplePay();
    }
  }, {
    immediate: true,
  });

  // Prefill address and consent from profile (logged-in checkout)
  watch(profileUser, (user) => {
    checkoutForm.value.shippingAddress.firstName = user?.firstName ?? '';
    checkoutForm.value.shippingAddress.lastName = user?.lastName ?? '';
    checkoutForm.value.shippingAddress.phone = profileShippingAddress.value?.phone ?? '';
    checkoutForm.value.shippingAddress.line1 = profileShippingAddress.value?.addressLine1 ?? '';
    checkoutForm.value.shippingAddress.line2 = profileShippingAddress.value?.addressLine2 ?? '';
    checkoutForm.value.shippingAddress.city = profileShippingAddress.value?.locality ?? '';
    checkoutForm.value.shippingAddress.zip = profileShippingAddress.value?.postalCode ?? '';
    checkoutForm.value.shippingAddress.state_code = profileShippingAddress.value?.administrativeArea ?? '';
    checkoutForm.value.hostedPageCheckout = false;

    if (!user) {
      return;
    }

    const smsBlocked = user.smsStop === true;
    consentForm.value.consentEmailGeneralMarketing = user.consentEmailGeneralMarketing ?? false;
    consentForm.value.consentEmailPersonalizedHealth = user.consentEmailPersonalizedHealth ?? false;
    consentForm.value.consentSmsMarketing = smsBlocked ? false : (user.consentSmsMarketing ?? false);
    consentForm.value.consentSmsTransactional = smsBlocked ? false : (user.consentSmsTransactional ?? false);
  });

  // Add an object for the billing address
  watch(isBillingAddressSame, (isSame) => {
    checkoutForm.value.billingAddress = !isSame ? {
      firstName: '',
      lastName: '',
      phone: '',
      line1: '',
      line2: '',
      city: '',
      zip: '',
      state_code: '',
    } : null;

    if (!checkoutForm.value.billingAddress) {
      return;
    }

    checkoutForm.value.billingAddress.firstName = profileUser.value?.firstName ?? '';
    checkoutForm.value.billingAddress.lastName = profileUser.value?.lastName ?? '';
    checkoutForm.value.billingAddress.phone = profileBillingAddress.value?.phone ?? '';
    checkoutForm.value.billingAddress.line1 = profileBillingAddress.value?.addressLine1 ?? '';
    checkoutForm.value.billingAddress.line2 = profileBillingAddress.value?.addressLine2 ?? '';
    checkoutForm.value.billingAddress.city = profileBillingAddress.value?.locality ?? '';
    checkoutForm.value.billingAddress.zip = profileBillingAddress.value?.postalCode ?? '';
    checkoutForm.value.billingAddress.state_code = profileBillingAddress.value?.administrativeArea ?? '';
  }, {
    immediate: true,
  });

  // Update paymentSourceId and submissionId
  watch([
    activePaymentMethodId,
    () => quizStore.submissionId,
  ], ([
    activePaymentMethodId,
    submissionId,
  ]) => {
    checkoutForm.value.paymentSourceId = activePaymentMethodId;
    checkoutForm.value.submissionId = submissionId!;
  }, { immediate: true });

  const mainVariant = computed(() => {
    return mainProduct.value?.product.variants.find((variant) => {
      return variant.variantType === VARIANT_TYPE.SUBSCRIPTION
        && variant.displayInQuiz
        && variant.variantPeriod
        && variant.variantPeriod === variantForm.value.period.value
        && variant.variantPeriodUnit === variantForm.value.period.unit;
    });
  });

  const membershipVariant = computed(() => {
    return membershipProduct.value?.product.variants.find((variant) => {
      return variant.variantType === VARIANT_TYPE.SINGLE
          && variant.displayInQuiz;
    });
  });

  const cartItems = computed(() => {
    if (!mainProduct.value) {
      return [];
    }

    return [
      {
        productId: mainProduct.value.product.id,
        variantId: mainVariant.value?.id || null,
        variantAmount: mainVariant.value?.variantAmount || 0,
        quantity: 1,
        dosageUnit: mainProduct.value.product.dosageUnit,
        shippingPrice: mainProduct.value.product.shippingPrices[0]?.shippingAmount || 0,
      },
      {
        productId: membershipProduct.value?.product.id as number,
        variantId: membershipVariant.value?.id || null,
        variantAmount: membershipVariant.value?.variantAmount || 0,
        quantity: 1,
        dosageUnit: membershipProduct.value?.product.dosageUnit || undefined,
        shippingPrice: membershipProduct.value?.product.shippingPrices[0]?.shippingAmount || 0,
      },
    ].filter((item) => item.productId && item.variantId);
  });

  const cartPayloadItems = computed<CartItemPayload[]>(() => {
    return cartItems.value.map((item) => omit(item, ['variantAmount', 'shippingPrice']));
  });

  watch(cartPayloadItems, (items) => {
    checkoutForm.value.items = items;
  });

  watch(applePayPaymentIntentId, (id) => {
    checkoutForm.value.paymentIntentId = id;
  });

  // Prepared checkout form for sending to the server
  const payloadCheckoutForm = computed(() => {
    const form = cloneDeep(checkoutForm.value);
    set(form, 'shippingAddress.phone', form.shippingAddress.phone.replace(/[^\d+]/g, ''));
    if (!isBillingAddressSame.value) {
      set(form, 'billingAddress.phone', form.billingAddress?.phone.replace(/[^\d+]/g, ''));
    }
    return {
      ...form,
      // Quiz non-PHI consent is captured at signup; omit here to avoid accidental opt-out.
      consent: {
        consentEmailGeneralMarketing: consentForm.value.consentEmailGeneralMarketing,
        consentEmailPersonalizedHealth: consentForm.value.consentEmailPersonalizedHealth,
        consentSmsMarketing: consentForm.value.consentSmsMarketing,
        consentSmsTransactional: consentForm.value.consentSmsTransactional,
      },
    };
  });

  // Coupon and estimation
  const couponInput = ref('');
  const estimateApi = useFetch(apiCart.estimate);
  const estimateData = computed(() => estimateApi.response.value?.data);
  const estimateLoading = computed(() => estimateApi.loading.value);
  const estimateError = computed(() => estimateApi.error.value);

  const estimate = async () => {
    if (!couponInput.value) {
      const searchParams = new URLSearchParams(location.search);
      couponInput.value = searchParams.get('coupon') ?? '';
    }

    await estimateApi.execute({
      // ...omit(payloadCheckoutForm.value, ['tokenId', 'paymentSourceId']),
      ...payloadCheckoutForm.value,
      hostedPageCheckout: false,
      coupon: couponInput.value,
    });

    if (
      estimateError.value
      || !couponInput.value
      || !estimateData.value?.coupon_applied
    ) {
      return;
    }

    checkoutForm.value.coupon = couponInput.value;
    couponInput.value = '';
  };

  const removeCoupon = () => {
    checkoutForm.value.coupon = '';
    couponInput.value = '';
  };

  const totalPrice = computed(() => {
    return sumBy(estimateData.value?.estimation.next_invoice_estimate?.line_items, 'amount');
  });

  const discountedTotalPrice = computed(() => {
    return totalPrice.value - sumBy(estimateData.value?.estimation.next_invoice_estimate?.line_items, 'discount_amount');
  });

  const nowPrice = computed(() => {
    return sumBy(estimateData.value?.estimation.invoice_estimate?.line_items, 'amount');
  });

  const discountedNowPrice = computed(() => {
    return nowPrice.value - sumBy(estimateData.value?.estimation.invoice_estimate?.line_items, 'discount_amount');
  });

  watch(cartItems, () => {
    if (!mainVariant.value) return;
    estimate();
  });

  // Buy now
  const buyNowApi = useFetch(apiCart.buyNow);
  const buyNowLoading = ref(false);

  const scrollToError = async () => {
    await nextTick();
    const errorSection = document.querySelector('.validation-error');
    errorSection?.scrollIntoView({ behavior: 'smooth' });
  };

  const validateNewPayment = () => new Promise((resolve, reject) => {
    if (!isNewPaymentMethod.value) {
      resolve(true);
      return;
    }
    cardComponent.value?.tokenize()
      .then((data: { token: string }) => {
        checkoutForm.value.tokenId = data.token;
        resolve(true);
      })
      .catch((e: string) => reject(e));
  });

  const buyNow = async () => {
    buyNowLoading.value = true;

    const validateCard = async () => {
      const validation = await Promise.all([
        checkout$.value.paymentSourceId.$validate(),
        validateNewPayment().catch((e: string) => {
          toast.error(e);
          return false;
        }),
      ]);

      if (validation.some((v) => !v)) {
        buyNowLoading.value = false;
        scrollToError();
        return;
      }
    };

    switch (paymentMethodType.value) {
      case PaymentMethodTypes.Card:
        await validateCard();
        break;
      case PaymentMethodTypes.ApplePay:
        // We can use some logic here
        break;
    }

    try {
      const response = await buyNowApi.execute(payloadCheckoutForm.value);

      const URLSearchParam = new URL(response!.data.url).searchParams;
      quizStore.paymentState = URLSearchParam.get('payment-state');
      quizStore.checkoutId = URLSearchParam.get('id');

      quizStore.paid = true;

      if (consentForm.value.consentEmailGeneralMarketing && userStore.info?.email) {
        pushDataLayerEvent('MarketingGenericConsent', userStore.info.email).catch(() => {});
      }

      const checkoutId = quizStore.checkoutId || crypto.randomUUID();

      const cartResponse = await apiCart.get();
      const cart = cartResponse.data;

      if (!cart) {
        return;
      }

      // Refresh profile store and re-init analytics with new data
      await userStore.refreshUserProfile();

      analyticsStore.purchase(
        cartStore.toAnalyticProducts(cart.items),
        checkoutId,
        cart.total / 100,
        cart.id,
      );
    } finally {
      buyNowLoading.value = false;
    }
  };

  return {
    paymentMethodType,
    instance,
    applePayHandler,
    initApplePay,
    applePayPaymentIntentId,
    isApplePayInited,
    createApplePaymentIntentLoading,
    isApplePayCompatible,
    products,
    mainProduct,
    membershipProduct,
    checkoutForm,
    consentForm,
    isBillingAddressSame,
    checkout$,
    variantForm,
    variant$,
    checkboxesForm,
    checkboxes$,
    couponInput,
    removeCoupon,
    estimate,
    estimateLoading,
    totalPrice,
    discountedTotalPrice,
    nowPrice,
    discountedNowPrice,
    cartItems,
    buyNow,
    buyNowLoading,
    cardComponent,
    paymentSection,
    paymentMethods,
    isNewPaymentMethod,
    activePaymentMethodId,
    deletePaymentMethod,
    scrollToError,
  };
});
